import { clearWorkspaceScopedClientState } from "@/state/workspaceClientState";

const MISMATCH_MESSAGE_RE = /does not belong to your organization/i;

/** UNKNOWN-WS-404-1 (Step B) contract: unknown, foreign and non-member workspaces → 404 with this code. */
const WORKSPACE_NOT_FOUND_CODE = "WORKSPACE_NOT_FOUND";

declare module "axios" {
  interface AxiosRequestConfig {
    /**
     * Set this on any request about a workspace other than the active one.
     *
     * WS-RECOVERY-404-1 (TR-2026-10-20-14c): a 403/404 from such a request (an invite
     * link's workspace, a join token's workspace) says nothing about the user's active
     * selection, so both API clients skip the stale-workspace recovery for it. The
     * clients cannot tell this themselves: the x-workspace-id header always carries
     * the active id, and a join names its workspace only in a body token.
     * Residual risk until UNKNOWN-WS-404-1 gives those endpoints their own error code:
     * a new call about a non-active workspace that forgets this flag can clear the
     * selection.
     */
    skipStaleWorkspaceRecovery?: boolean;
  }
}

/** True when the failed request opted out of stale-workspace recovery. Read by both API clients. */
export function isStaleWorkspaceRecoverySkipped(config: unknown): boolean {
  return (config as { skipStaleWorkspaceRecovery?: unknown } | null | undefined)?.skipStaleWorkspaceRecovery === true;
}

function readStatus(err: unknown): number | undefined {
  if (!err || typeof err !== "object") return undefined;
  const e = err as {
    status?: unknown;
    response?: { status?: unknown };
  };
  if (typeof e.status === "number") return e.status;
  if (typeof e.response?.status === "number") return e.response.status;
  return undefined;
}

/*
 * WS-RECOVERY-404-1 (TR-2026-10-20-14a): the server's body ({ code, message }, the
 * flat API error envelope) is read BEFORE the error object's own fields. A raw
 * AxiosError always carries its own `message` ("Request failed with status code
 * 403") and `code` ("ERR_BAD_REQUEST"); reading those first meant lib/api.ts,
 * which passes the raw AxiosError, never matched, so its recovery never fired.
 */
function readMessage(err: unknown): string {
  if (!err || typeof err !== "object") return "";
  const e = err as {
    message?: unknown;
    response?: { data?: { message?: unknown } };
  };
  const bodyMsg = e.response?.data?.message;
  if (typeof bodyMsg === "string") return bodyMsg;
  if (typeof e.message === "string") return e.message;
  return "";
}

function readCode(err: unknown): string {
  if (!err || typeof err !== "object") return "";
  const e = err as {
    code?: unknown;
    response?: { data?: { code?: unknown } };
  };
  const bodyCode = e.response?.data?.code;
  if (typeof bodyCode === "string") return bodyCode;
  if (typeof e.code === "string") return e.code;
  return "";
}

/**
 * A stale x-workspace-id is signalled two ways; both invalidate the client cache:
 * - today: 403 with message "Workspace … does not belong to your organization"
 *   (TenantContextInterceptor and others; the code varies by producer);
 * - Step B (UNKNOWN-WS-404-1): 404 with code WORKSPACE_NOT_FOUND. Matched on status
 *   + code only, never the message, so other 404s do not trigger recovery.
 */
export function isWorkspaceOrgMismatchError(err: unknown): boolean {
  const status = readStatus(err);
  if (status === 404) return readCode(err) === WORKSPACE_NOT_FOUND_CODE;
  if (status !== 403) return false;
  const message = readMessage(err);
  if (!MISMATCH_MESSAGE_RE.test(message)) return false;
  const code = readCode(err);
  // Exclude CSRF 403s (same status family, different meaning).
  if (code.startsWith("CSRF_") || /csrf/i.test(message)) return false;
  return true;
}

/** Clear persisted + in-memory workspace refs when a stale-workspace 403 or 404 is observed. */
export function invalidateStaleWorkspaceOnMismatch(err: unknown): boolean {
  if (!isWorkspaceOrgMismatchError(err)) return false;
  clearWorkspaceScopedClientState();
  return true;
}
